import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons/faInfoCircle';
import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import PropTypes from 'prop-types';

import { Popover } from '@mantine/core';

import { CommonPopover } from '@shipstation/client-common/components/common-popover';

import { MDash } from 'ui-components/components/mdash/mdash';
import { Text, TextType } from 'ui-components/components/text';
import { FlexRow } from 'ui-components/components/flex';
import { useMaxZ } from 'ui-components/hooks/services/use-max-z';

import Global from '../../store/global-selectors';
import { optionTypes } from '../../delivery-options/constants';
import { FormattedCurrency } from '../../components/formatted-currency';
import { messages } from './popover-messages';

import style from './requested-service-popover.module.css';

const renderDeliveryTime = (minTransitDays, maxTransitDays, optionType) => {
  if (optionType === optionTypes['local-delivery'].value) {
    return (
      <span>
        <FormattedMessage {...messages.deliveryTimeSameDay} />
      </span>
    );
  }
  if (minTransitDays && maxTransitDays) {
    return (
      <span>
        <FormattedMessage
          {...messages.deliveryTimeRange}
          values={{
            maxNumberOfDays: maxTransitDays,
            minNumberOfDays: minTransitDays,
          }}
        />
      </span>
    );
  }
  if (maxTransitDays) {
    return (
      <span>
        <FormattedMessage {...messages.deliveryTimeMaxDays} values={{ numberOfDays: maxTransitDays }} />
      </span>
    );
  }
  return <span>&mdash;</span>;
};

export const RequestedServicePopover = ({ deliveryOptions }) => {
  const { summary, optionType, shipments } = deliveryOptions;
  const { minCalendarTransitDays = null, maxCalendarTransitDays = null } = summary.deliveryTimeEstimate;
  const [isOpen, setIsOpen] = useState(false);
  const zIndex = useMaxZ();
  const intl = useIntl();

  const closePopover = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  const togglePopover = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen, setIsOpen]);

  const onOpenChange = useCallback((nextOpen) => (nextOpen ? undefined : closePopover()), [closePopover]);

  const target = () => {
    return (
      <div className={style['target-icon']} onClick={togglePopover} title={intl.formatMessage(messages.titleCR)}>
        <FontAwesomeIcon icon={faInfoCircle} />
      </div>
    );
  };
  const sellerShippingServices = useSelector(Global.selectSellerServices) || [];
  const matchingOptionType = Object.keys(optionTypes).find((key) => optionTypes[key].id === optionType.id);
  const OptionTypeDisplayName = optionTypes[matchingOptionType]?.displayName;

  const bodyContent = (
    <div className={style.popover}>
      <div className={style['title-section']}>
        <span className={style['title-text']}>{summary.displayName}</span>
        <FormattedMessage {...messages.subtitleCR} />
      </div>
      <div className={style['shipment-container']}>
        {shipments.map((s, i) => {
          const serviceId = s.shippingServiceId || null;
          const { name: serviceName } = sellerShippingServices.find((serv) => serv.serviceId === `${serviceId}`) || {};
          return (
            <div key={serviceId} className={style['body-section']}>
              {/* <div className={i !== shipments.length - 1 ? style.divider : ''}> */}
              <div className={style['body-row']}>
                <Text
                  className={style.label}
                  formattedMessageValues={{
                    number: i + 1,
                    total: shipments.length,
                  }}
                  textType={TextType.Paragraph}
                  value={messages.shipments}
                />
              </div>
              <div className={style['body-row']}>
                <Text className={style.label} textType={TextType.Paragraph} value={messages.service} />
                {serviceName ? <span>{serviceName}</span> : <MDash />}
              </div>
              <div className={style['body-row']}>
                <Text className={style.label} textType={TextType.Paragraph} value={messages.optionType} />
                {OptionTypeDisplayName || <MDash />}
              </div>
              <div className={style['body-row']}>
                <Text className={style.label} textType={TextType.Paragraph} value={messages.deliveryTimeTitle} />
                {renderDeliveryTime(minCalendarTransitDays, maxCalendarTransitDays, matchingOptionType)}
              </div>
              <div className={style['body-row']}>
                <Text className={style.label} textType={TextType.Paragraph} value={messages.shippingCost} />
                {typeof s.shipmentCost?.amount === 'number' ? (
                  <span>
                    <FormattedCurrency currencyCode={s.shipmentCost.currency} value={s.shipmentCost.amount} />
                  </span>
                ) : (
                  <MDash />
                )}
              </div>
            </div>
          );
        })}
      </div>
      <FlexRow className={style.summary}>
        <Text className={style.label} textType={TextType.Paragraph} value={messages.totalCost} />
        {typeof summary.totalCost?.amount === 'number' ? (
          <span className={style['total-amount']}>
            <FormattedCurrency currencyCode={summary.totalCost.currency} value={summary.totalCost.amount} />
          </span>
        ) : (
          <MDash />
        )}
      </FlexRow>
    </div>
  );

  return (
    <CommonPopover
      classNames={{ dropdown: style['requested-service-dropdown'], arrow: style['requested-service-arrow'] }}
      onChange={onOpenChange}
      opened={isOpen}
      position='bottom'
      withArrow
      zIndex={zIndex}
    >
      <Popover.Target>{target()}</Popover.Target>
      <Popover.Dropdown>{bodyContent}</Popover.Dropdown>
    </CommonPopover>
  );
};

RequestedServicePopover.propTypes = {
  deliveryOptions: PropTypes.shape({
    summary: {
      displayName: PropTypes.string,
      description: PropTypes.string,
      deliveryTimeEstimate: {
        maxCalendarTransitDays: PropTypes.number,
        minCalendarTransitDays: PropTypes.number,
      },
      cost: {
        value: PropTypes.number,
        code: PropTypes.string,
      },
      totalCosat: {
        amount: PropTypes.number,
        currency: PropTypes.string,
      },
    },
    optionType: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    }),
    shipments: PropTypes.array,
  }).isRequired,
};
